import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Team collaboration carousel images
  const backgroundImages = [
    '/assets/images/team_collaboration_presentation_one.png',
    '/assets/images/team_collaboration_meeting_two.png', 
    '/assets/images/strategic_planning_collaboration_three.png',
    '/assets/images/business_presentation_team_four.png'
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % backgroundImages?.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-24 overflow-hidden min-h-[90vh] flex items-center">
      {/* Team Collaboration Carousel Background - Made Less Bright */}
      <div className="absolute inset-0 z-0">
        {backgroundImages?.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-25' : 'opacity-0'
            }`}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-gray-900/95 via-gray-800/90 to-gray-900/95" style={{
              background: `linear-gradient(135deg, 
                rgba(217, 59, 52, 0.70) 0%, 
                rgba(155, 142, 86, 0.60) 50%, 
                rgba(255, 215, 0, 0.45) 100%)`
            }}></div>
            <img 
              src={image} 
              alt={`Professional team collaboration scene ${index + 1} showing workplace presentations, strategic planning sessions, and business transformation discussions with diverse professionals working together`}
              className="w-full h-full object-cover" 
            />
          </div>
        ))}
        
        {/* Reduced Pattern Overlay Brightness */}
        <div className="absolute inset-0 bg-gradient-to-r from-red-900/10 via-transparent to-yellow-900/10" style={{
          background: `linear-gradient(90deg, 
            rgba(217, 59, 52, 0.08) 0%, 
            rgba(155, 142, 86, 0.05) 50%, 
            rgba(255, 215, 0, 0.08) 100%)`
        }}></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(217, 59, 52, 0.06) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(155, 142, 86, 0.06) 0%, transparent 50%),
                           radial-gradient(circle at 50% 50%, rgba(255, 215, 0, 0.04) 0%, transparent 30%)`
        }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content with Brand Colors - Toned Down */}
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-3 rounded-xl shadow-lg" style={{
                  background: `linear-gradient(135deg, rgba(217, 59, 52, 0.8) 0%, rgba(155, 142, 86, 0.6) 100%)`
                }}>
                  <Icon name="Eye" size={28} className="text-white" />
                </div>
                <span className="font-bold text-base uppercase tracking-wide px-4 py-2 rounded-full border" style={{
                  backgroundColor: 'rgba(217, 59, 52, 0.15)',
                  color: '#E6C068',
                  borderColor: 'rgba(155, 142, 86, 0.4)'
                }}>
                  Notre Vision Transformationnelle
                </span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                Partenaire 
                <span className="text-transparent bg-clip-text block mt-2" style={{
                  background: `linear-gradient(135deg, rgba(217, 59, 52, 0.9) 0%, rgba(155, 142, 86, 0.8) 50%, rgba(255, 215, 0, 0.9) 100%)`,
                  WebkitBackgroundClip: 'text'
                }}>
                  Stratégique
                </span>
                <span className="text-4xl md:text-5xl text-gray-300 block mt-2">de Référence</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-400 leading-relaxed max-w-2xl">
                Transformons ensemble vos défis en <span className="font-semibold" style={{color: '#E6C068'}}>opportunités de croissance durable</span> 
                grâce à l'excellence technologique et l'Intelligence Artificielle.
              </p>
            </div>

            {/* Vision Stats Enhanced with Toned Down Brand Colors */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="backdrop-blur-sm rounded-2xl p-8 border transition-all duration-300 group" style={{
                background: `linear-gradient(135deg, rgba(217, 59, 52, 0.2) 0%, rgba(155, 142, 86, 0.15) 100%)`,
                borderColor: 'rgba(217, 59, 52, 0.3)'
              }}>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="p-3 rounded-xl group-hover:scale-110 transition-transform" style={{
                    background: `linear-gradient(135deg, rgba(155, 142, 86, 0.8) 0%, rgba(255, 215, 0, 0.8) 100%)`
                  }}>
                    <Icon name="TrendingUp" size={28} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold">Croissance Durable</h3>
                </div>
                <p className="text-gray-400 leading-relaxed">
                  Transformation stratégique des défis en opportunités de développement à long terme
                </p>
              </div>
              
              <div className="backdrop-blur-sm rounded-2xl p-8 border transition-all duration-300 group" style={{
                background: `linear-gradient(135deg, rgba(155, 142, 86, 0.2) 0%, rgba(255, 215, 0, 0.15) 100%)`,
                borderColor: 'rgba(255, 215, 0, 0.3)'
              }}>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="p-3 rounded-xl group-hover:scale-110 transition-transform" style={{
                    background: `linear-gradient(135deg, rgba(255, 215, 0, 0.8) 0%, rgba(217, 59, 52, 0.8) 100%)`
                  }}>
                    <Icon name="Lightbulb" size={28} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold">Innovation IA</h3>
                </div>
                <p className="text-gray-400 leading-relaxed">
                  Intégration éthique des technologies émergentes pour l'excellence opérationnelle
                </p>
              </div>
            </div>

            {/* CTA Buttons Enhanced with Toned Down Brand Colors */}
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6 pt-4">
              <Button
                variant="default"
                size="lg"
                className="text-white px-10 py-5 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl border"
                style={{
                  background: `linear-gradient(135deg, rgba(217, 59, 52, 0.8) 0%, rgba(155, 142, 86, 0.8) 100%)`,
                  borderColor: 'rgba(217, 59, 52, 0.5)'
                }}
              >
                <Icon name="MessageSquare" size={24} className="mr-3" />
                Consultation Stratégique
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                className="border-2 px-10 py-5 rounded-xl font-bold text-lg transition-all duration-300 backdrop-blur-sm"
                style={{
                  borderColor: 'rgba(255, 215, 0, 0.5)',
                  color: '#E6C068',
                  backgroundColor: 'rgba(255, 215, 0, 0.08)'
                }}
              >
                <Icon name="Download" size={24} className="mr-3" />
                Guide Vision 2024
              </Button>
            </div>
          </div>

          {/* Enhanced Visual Elements with Toned Down Brand Colors */}
          <div className="relative">
            {/* Main Vision Circle with Dynamic Elements */}
            <div className="relative w-96 h-96 mx-auto">
              {/* Animated Background Rings - Less Bright */}
              <div className="absolute inset-0 rounded-full animate-pulse" style={{
                background: `linear-gradient(135deg, rgba(217, 59, 52, 0.2) 0%, rgba(155, 142, 86, 0.2) 100%)`
              }}></div>
              <div className="absolute inset-4 rounded-full animate-pulse" style={{
                background: `linear-gradient(135deg, rgba(155, 142, 86, 0.25) 0%, rgba(255, 215, 0, 0.2) 100%)`,
                animationDelay: '1s'
              }}></div>
              <div className="absolute inset-8 rounded-full animate-pulse" style={{
                background: `linear-gradient(135deg, rgba(255, 215, 0, 0.3) 0%, rgba(217, 59, 52, 0.2) 100%)`,
                animationDelay: '2s'
              }}></div>
              
              {/* Center Vision Icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="backdrop-blur-sm rounded-full p-12 border-2 shadow-2xl" style={{
                  background: `linear-gradient(135deg, rgba(217, 59, 52, 0.15) 0%, rgba(155, 142, 86, 0.15) 100%)`,
                  borderColor: 'rgba(255, 215, 0, 0.3)'
                }}>
                  <Icon name="Target" size={100} style={{color: '#E6C068'}} />
                </div>
              </div>

              {/* Enhanced Floating Elements with Toned Down Brand Colors */}
              <div className="absolute -top-6 left-8 backdrop-blur-sm rounded-2xl p-6 border animate-float shadow-xl" style={{
                background: `linear-gradient(135deg, rgba(255, 215, 0, 0.7) 0%, rgba(217, 59, 52, 0.6) 100%)`,
                borderColor: 'rgba(255, 255, 255, 0.25)'
              }}>
                <div className="flex items-center space-x-3">
                  <Icon name="Award" size={24} className="text-white" />
                  <span className="font-bold text-white">Excellence</span>
                </div>
              </div>
              
              <div className="absolute top-8 -right-6 backdrop-blur-sm rounded-2xl p-6 border animate-float shadow-xl" style={{
                background: `linear-gradient(135deg, rgba(155, 142, 86, 0.7) 0%, rgba(255, 215, 0, 0.6) 100%)`,
                borderColor: 'rgba(255, 255, 255, 0.25)',
                animationDelay: '1s'
              }}>
                <div className="flex items-center space-x-3">
                  <Icon name="Handshake" size={24} className="text-white" />
                  <span className="font-bold text-white">Partenariat</span>
                </div>
              </div>
              
              <div className="absolute -bottom-6 right-8 backdrop-blur-sm rounded-2xl p-6 border animate-float shadow-xl" style={{
                background: `linear-gradient(135deg, rgba(217, 59, 52, 0.7) 0%, rgba(155, 142, 86, 0.6) 100%)`,
                borderColor: 'rgba(255, 255, 255, 0.25)',
                animationDelay: '2s'
              }}>
                <div className="flex items-center space-x-3">
                  <Icon name="Zap" size={24} className="text-white" />
                  <span className="font-bold text-white">Innovation</span>
                </div>
              </div>
              
              <div className="absolute bottom-8 -left-6 backdrop-blur-sm rounded-2xl p-6 border animate-float shadow-xl" style={{
                background: `linear-gradient(135deg, rgba(155, 142, 86, 0.7) 0%, rgba(217, 59, 52, 0.6) 100%)`,
                borderColor: 'rgba(255, 255, 255, 0.25)',
                animationDelay: '0.5s'
              }}>
                <div className="flex items-center space-x-3">
                  <Icon name="TrendingUp" size={24} className="text-white" />
                  <span className="font-bold text-white">Croissance</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Vision Domains with Toned Down Brand Colors */}
        <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { name: 'RH Digitales', icon: 'Users', path: '/human-resources-management-services', color: `linear-gradient(135deg, rgba(217, 59, 52, 0.8) 0%, rgba(155, 142, 86, 0.8) 100%)` },
            { name: 'Marketing Digital', icon: 'Megaphone', path: '/digital-marketing-services', color: `linear-gradient(135deg, rgba(155, 142, 86, 0.8) 0%, rgba(255, 215, 0, 0.8) 100%)` },
            { name: 'Projets Numériques', icon: 'Monitor', path: '/digital-project-management-services', color: `linear-gradient(135deg, rgba(255, 215, 0, 0.8) 0%, rgba(217, 59, 52, 0.8) 100%)` },
            { name: 'Intelligence Artificielle', icon: 'Brain', path: '/ai-solutions-integration-hub', color: `linear-gradient(135deg, rgba(217, 59, 52, 0.8) 0%, rgba(255, 215, 0, 0.8) 100%)` }
          ]?.map((domain, index) => (
            <div
              key={domain?.name}
              className="backdrop-blur-sm rounded-2xl p-8 border transition-all duration-300 cursor-pointer group hover:transform hover:scale-105"
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                borderColor: 'rgba(255, 255, 255, 0.15)'
              }}
            >
              <div className="text-center space-y-4">
                <div className="rounded-2xl p-4 w-fit mx-auto group-hover:scale-110 transition-transform shadow-lg" style={{
                  background: domain?.color
                }}>
                  <Icon name={domain?.icon} size={32} className="text-white" />
                </div>
                <h3 className="font-bold text-lg text-white">{domain?.name}</h3>
                <div className="w-12 h-1 rounded-full mx-auto opacity-0 group-hover:opacity-100 transition-opacity" style={{
                  background: `linear-gradient(90deg, rgba(217, 59, 52, 0.8) 0%, rgba(255, 215, 0, 0.8) 100%)`
                }}></div>
              </div>
            </div>
          ))}
        </div>

        {/* Carousel Indicators with Brand Colors */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3">
          {backgroundImages?.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'scale-125' : 'hover:opacity-80'
              }`}
              style={{
                backgroundColor: index === currentSlide ? '#E6C068' : 'rgba(255, 255, 255, 0.4)'
              }}
            />
          ))}
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(1deg); }
          66% { transform: translateY(-5px) rotate(-1deg); }
        }
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;